<template>
  <modal name="ad-consent-modal" :adaptive="true" :scrollable="true" height="auto" :width="460"
    @opened="onModalOpened" @closed="onModalClosed">
    <div class="v--modal-close-top">
      <el-button class="adconsent_close" @click="closeModal" icon="el-icon-circle-close" style="font-size: 2rem;"
        type="text"></el-button>
    </div>
    <div class="adconsent_popup">
      <h3 class="adconsent_h3">プライバシーポリシー改定のお知らせ</h3>

<div class="adconsent_body" ref="scrollBody" @scroll="onBodyScroll">
        <p class="adconsent_txt">このたびプライバシーポリシーを改定し、広告配信の最適化のため、サイトの利用状況を広告事業者（Meta等）へ送信する機能を追加しました。</p>
        <p class="adconsent_txt">ご同意いただいた場合のみ有効化され、マイページからいつでも停止できます。詳しくは<a href="https://www.yagish.jp/privacy/" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>をご確認ください。</p>
      </div>

      <p v-if="!scrolledToBottom" class="adconsent_note">※同意いただける場合は、最下部まで<br>スクロールして同意を押下してください。</p>
      <button class="adconsent_btn" :class="{ 'is-active': scrolledToBottom }"
        :disabled="!scrolledToBottom" @click="onAgreeAll">同意する</button>
    </div>
  </modal>
</template>

<script>
import { AD_NETWORKS } from '@/config/ad-networks'
import {
  getConsentFlag, setConsentFlag, getUndecidedNetworks,
  fetchAllFlagsFromServer, saveConsentFlagToServer, fireNetwork
} from '@/utils/ad-consent'

export default {
  name: 'AdConsentModal',
  data () {
    return {
      undecidedNetworks: [],
      modalShown: false,
      decisionMade: false,
      scrolledToBottom: false,
    }
  },
  async mounted () {
    // ログイン済みの場合は auth_state_changed で DB チェック
    this.$eventHub.$on('auth_state_changed', this.onAuthStateChanged)
    // 非ログインの場合は firebase-initialized(null) で Cookie チェック
    this.$eventHub.$on('firebase-initialized', this.onFirebaseInitialized)
    this.$eventHub.$on('show-ad-consent-modal', this.showAll)
  },
  destroyed () {
    this.$eventHub.$off('auth_state_changed', this.onAuthStateChanged)
    this.$eventHub.$off('firebase-initialized', this.onFirebaseInitialized)
    this.$eventHub.$off('show-ad-consent-modal', this.showAll)
  },
  methods: {
    /** ログイン済み: DB優先でCookieと同期 → 未決定があればモーダル表示 */
    async onAuthStateChanged () {
      const uid = this.$firebase.user.uid
      // まず DB から取得（DB の値が正）
      const flags = await fetchAllFlagsFromServer(this.$axios, this.$varuna, uid)
      // DB に値がないネットワークのみ Cookie → DB に同期
      for (const network of AD_NETWORKS) {
        const dbVal = flags[network.id]
        if (dbVal === '1' || dbVal === '0') continue // DB 優先、上書きしない
        const cookieVal = getConsentFlag(network.id)
        if (cookieVal === '1' || cookieVal === '0') {
          await saveConsentFlagToServer(this.$axios, this.$varuna, uid, network.id, cookieVal === '1')
          flags[network.id] = cookieVal // ローカルにも反映
        }
      }
      // 同期済みなら再表示しない
      if (this.modalShown) return
      this.undecidedNetworks = AD_NETWORKS.filter(n => {
        const val = flags[n.id]
        return val !== '1' && val !== '0'
      })
      if (this.undecidedNetworks.length > 0) {
        this.modalShown = true
        this.$modal.show('ad-consent-modal')
      }
    },
    /** 非ログイン: Cookie から未決定ネットワークがあれば表示 */
    onFirebaseInitialized () {
      if (this.modalShown || this.$firebase.isAuthenticated) return
      this.undecidedNetworks = getUndecidedNetworks()
      if (this.undecidedNetworks.length > 0) {
        this.modalShown = true
        this.$modal.show('ad-consent-modal')
      }
    },
    showAll () {
      this.undecidedNetworks = AD_NETWORKS
      this.$modal.show('ad-consent-modal')
    },
    /** モーダル表示時: 決定・スクロール状態をリセット。内容が短くスクロール不要なら即活性化 */
    onModalOpened () {
      this.decisionMade = false
      this.scrolledToBottom = false
      this.$nextTick(() => {
        const el = this.$refs.scrollBody
        if (!el) return
        if (el.scrollHeight <= el.clientHeight + 2) this.scrolledToBottom = true
      })
    },
    /** 本文を最下部までスクロールしたら「同意する」を活性化 */
    onBodyScroll () {
      const el = this.$refs.scrollBody
      if (!el) return
      this.scrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
    },
    async onAgreeAll () {
      this.decisionMade = true // 同意なので、閉じる際の未同意登録をスキップさせる
      for (const network of this.undecidedNetworks) {
        if (this.$firebase && this.$firebase.isAuthenticated) {
          await saveConsentFlagToServer(this.$axios, this.$varuna, this.$firebase.user.uid, network.id, true)
        } else {
          setConsentFlag(network.id, '1')
        }
        fireNetwork(network.id)
      }
      this.$modal.hide('ad-consent-modal')
    },
    /** ×ボタン用。閉じる操作に集約し、未同意登録は onModalClosed で行う */
    closeModal () {
      this.$modal.hide('ad-consent-modal')
    },
    /**
     * モーダルが閉じた時のハンドラ。
     * 同意（onAgreeAll）以外の閉じ方（×・オーバーレイ外側クリック/タップ・ESC 等）は
     * すべて「未同意('0')」として登録する。
     */
    async onModalClosed () {
      if (this.decisionMade) return
      this.decisionMade = true
      for (const network of this.undecidedNetworks) {
        if (this.$firebase && this.$firebase.isAuthenticated) {
          await saveConsentFlagToServer(this.$axios, this.$varuna, this.$firebase.user.uid, network.id, false)
        } else {
          setConsentFlag(network.id, '0')
        }
      }
    },
  },
}
</script>

<style scoped>
.v--modal-close-top {
  display: flex;
  justify-content: flex-end;
  height: 15px;
}

/* 他の要素を動かさず、×ボタンだけを下方向にずらす（数値で調整可） */
.adconsent_close {
  transform: translateY(18px);
  padding: 0; /* el-button の内側余白を除去し上部を詰める */
}

.adconsent_popup {
  padding: 10px;
}

.adconsent_h3 {
  font-weight: bold;
  font-size: 1.05rem;
  line-height: 1.5;
  color: #004D40;
  margin-bottom: 8px;
}


.adconsent_body {
  max-height: 220px;
  overflow-y: scroll;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 14px 16px;
  font-size: 0.875rem;
  line-height: 1.7;
  line-break: strict;
  text-wrap: pretty;
  color: #333;
  scrollbar-width: thin;
  scrollbar-color: #bbb #f0f0f0;
  margin-bottom: 16px;
}
.adconsent_body::-webkit-scrollbar { width: 6px; }
.adconsent_body::-webkit-scrollbar-track { background: #f0f0f0; border-radius: 3px; }
.adconsent_body::-webkit-scrollbar-thumb { background: #bbb; border-radius: 3px; }

.adconsent_subh {
  font-size: 0.9rem;
  font-weight: 700;
  color: #004D40;
  margin: 14px 0 8px;
}

.adconsent_list {
  padding-left: 1.25em;
  margin-bottom: 12px;
}
.adconsent_list li { margin-bottom: 8px; }

.adconsent_txt { margin-bottom: 10px; }
.adconsent_txt a { color: #4a90d9; text-decoration: underline; }

.adconsent_btn {
  display: block; /* inline-block 由来の下部余白（後続空白の行ボックス）を除去 */
  width: 100%;
  padding: 13px;
  background: #ccc;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 700;
  cursor: not-allowed;
  transition: background 0.2s;
}
.adconsent_btn.is-active {
  background: #0F9934;
  cursor: pointer;
}
.adconsent_btn.is-active:hover { background: #0a7a28; }

.adconsent_note {
  text-align: center;
  font-size: 0.75rem;
  line-height: 1.6;
  color: #888;
  margin-top: 6px;
}
</style>
